<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { type SelectOption } from 'naive-ui';
import { IconDocumentText } from '@/components/design/icons';
import { TaskStatus, type TemplateValidationError } from '@/api';
import { useOfficeTemplateStore } from '@/office/stores/officeTemplateStore';
import { flattenFields } from '@/utils/contractGeneration/templateSchemaFields';
import { extractErrorDetail } from '@/utils/errorHandler';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import TemplateFieldsCard from './TemplateFieldsCard.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaRemoteSelect from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaVariantChip from '@/components/design/domain/templating/GaiaVariantChip.vue';
import { templateValidationChipMap } from '@/components/design/statusMaps';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * "My templates" tab — browse existing templates, load one into the open
 * Word document, and inspect the fields of the STORED file (the backend's
 * saved extracted_schema). Authoring actions (test the live document, save
 * it back) live on the Editor tab.
 */

const store = useOfficeTemplateStore();
const message = useGaiaToast();

const templateOptions = computed<SelectOption[]>(() => {
  const options: SelectOption[] = store.templates.map(template => ({
    label: template.name,
    value: template.uid,
  }));
  // Keep the selected template's label visible even when a new search/page
  // no longer contains it — otherwise the select falls back to the raw uid.
  const selected = store.selectedTemplate;
  if (selected && !store.templates.some(template => template.uid === selected.uid)) {
    options.unshift({ label: selected.name, value: selected.uid });
  }
  return options;
});

/** Load the next page when the dropdown is scrolled near its bottom. */
function loadMoreTemplates(): void {
  void store.loadMoreTemplates();
}

const variantOptions = computed<SelectOption[]>(() =>
  store.variants.map(variant => ({
    label: `${variant.name} — ${variant.file_name}`,
    value: variant.uid,
  }))
);

/** The variant's validation state, through the same adapter the web pages use. */
const fileStatusChip = computed(() => {
  const status = store.selectedVariant?.template_status;
  return status ? templateValidationChipMap[status] : null;
});

const fileFailedValidation = computed(
  () => store.selectedVariant?.template_status === TaskStatus.FAILED
);

/** Mirrors the web variant-detail hero's "N fields extracted · M required". */
const fieldStats = computed<{ total: number; required: number }>(() => {
  const flat = flattenFields(store.selectedFileDetail?.extracted_schema?.fields ?? undefined);
  return { total: flat.length, required: flat.filter(field => field.required).length };
});

const storedValidationErrors = computed<TemplateValidationError[]>(
  () => store.selectedFileDetail?.validation_errors ?? []
);

async function handleTemplateSearch(query: string): Promise<void> {
  try {
    await store.searchTemplates(query);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to search templates.'));
  }
}

// Always refresh on mount — templates/variants may have been added or removed
// from the web app while the task pane was on another tab.
onMounted(() => {
  void handleTemplateSearch('');
  void refreshVariants();
});

async function refreshVariants(): Promise<void> {
  try {
    await store.refreshVariants();
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to refresh template variants.'));
  }
}

async function handleTemplateSelect(uid: string | null): Promise<void> {
  try {
    await store.selectTemplate(uid);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to load template variants.'));
  }
}

async function handleOpenInDocument(): Promise<void> {
  try {
    await store.openInDocument();
    message.success('Template loaded into the document.');
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to open the template in the document.'));
  }
}

const fileIconColor = gaiaNeutral.textTertiary;
</script>

<template>
  <n-flex vertical :size="14" :wrap="false">
    <n-flex vertical :size="12" :wrap="false">
      <gaia-form-field label="Template">
        <gaia-remote-select
          :value="store.selectedTemplateUid"
          :options="templateOptions"
          :loading="store.templatesLoading"
          clearable
          placeholder="Search templates…"
          style="width: 100%"
          @search="handleTemplateSearch"
          @load-more="loadMoreTemplates"
          @update:value="handleTemplateSelect"
        />
      </gaia-form-field>
      <gaia-form-field v-if="store.selectedTemplateUid" label="Variant">
        <gaia-select
          :value="store.selectedVariantUid"
          :options="variantOptions"
          :loading="store.variantsLoading"
          clearable
          placeholder="Pick a variant…"
          style="width: 100%"
          @update:value="store.selectVariant"
        />
      </gaia-form-field>
    </n-flex>

    <gaia-empty-state v-if="!store.selectedVariant" dashed title="Pick a template">
      Choose a template and one of its variants to open it in this document or inspect its fields.
      To build a template from the current document, switch to the Editor tab.
    </gaia-empty-state>

    <template v-else>
      <!-- Compact version of the web variant-detail hero: variant chip + status
           chip on top, the file's identity, its stats, then the action. -->
      <gaia-card size="small">
        <n-flex vertical :size="12" :wrap="false">
          <n-flex align="center" justify="space-between" :wrap="false">
            <gaia-variant-chip :name="store.selectedVariant.name" />
            <gaia-status-chip v-if="fileStatusChip" :tone="fileStatusChip.tone">
              {{ fileStatusChip.label }}
            </gaia-status-chip>
          </n-flex>

          <n-flex align="center" :size="10" :wrap="false">
            <n-icon :size="20" :component="IconDocumentText" :color="fileIconColor" />
            <n-flex vertical :size="1" :style="{ minWidth: 0 }">
              <gaia-text
                size="body"
                weight="semibold"
                tone="ink"
                :style="{ overflowWrap: 'anywhere' }"
              >
                {{ store.selectedVariant.file_name }}
              </gaia-text>
              <gaia-text v-if="store.selectedTemplate" size="meta" tone="subtle">
                Variant of {{ store.selectedTemplate.name }}
              </gaia-text>
            </n-flex>
          </n-flex>

          <n-flex align="center" justify="space-between" :size="[10, 4]">
            <gaia-text size="micro" tone="subtle">
              {{ fieldStats.total }} field{{ fieldStats.total === 1 ? '' : 's' }} extracted
              <template v-if="fieldStats.required > 0">
                · {{ fieldStats.required }} required
              </template>
            </gaia-text>
            <gaia-text size="micro" tone="subtle">
              Created {{ formatDateForDisplay(store.selectedVariant.created_at) }}
            </gaia-text>
          </n-flex>

          <!-- Replacing the open document is the one destructive thing on this
               tab, so it gets the same anchored confirm the web uses. -->
          <gaia-popconfirm
            title="Replace this document?"
            confirm-label="Open template"
            placement="bottom"
            :width="256"
            @confirm="handleOpenInDocument"
          >
            <template #trigger>
              <gaia-button block size="small" :loading="store.openingInDocument">
                Open in document
              </gaia-button>
            </template>
            Everything currently in this Word document is replaced with the template.
          </gaia-popconfirm>
        </n-flex>
      </gaia-card>

      <gaia-banner
        v-if="fileFailedValidation && storedValidationErrors.length > 0"
        type="error"
        title="This template file failed validation"
      >
        <n-flex vertical :size="4">
          <gaia-text v-for="(item, index) in storedValidationErrors" :key="index" size="dense">
            {{ item.message }}
          </gaia-text>
        </n-flex>
      </gaia-banner>

      <n-spin :show="store.selectedFileDetailLoading">
        <template-fields-card
          :fields="store.selectedFileDetail?.extracted_schema?.fields"
          title="Fields in this template"
          default-expanded
        />
      </n-spin>
    </template>
  </n-flex>
</template>
